import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useToast } from '../contexts/ToastContext'
import { SCoinTransaction, APP_CONFIG } from '../types'
import { localDb, formatCurrency } from '../lib/db'
import { QRCodeSVG } from 'qrcode.react'
import './Wallet.css'

export default function Wallet() {
    const { user, updateUser } = useAuth()
    const { showToast } = useToast()
    const [transactions, setTransactions] = useState<SCoinTransaction[]>([])
    const [showBuyModal, setShowBuyModal] = useState(false)
    const [buyAmount, setBuyAmount] = useState(100)
    const [showPaymentQR, setShowPaymentQR] = useState(false)

    useEffect(() => {
        if (user) {
            const userTransactions = localDb.transactions.getByUserId(user.id)
            setTransactions(userTransactions)
        }
    }, [user])

    const scoinsBalance = user?.s_coins || 0
    const loyaltyPoints = user?.loyalty_points || 0

    // S-Coins to INR conversion for display
    const scoinsValue = Math.floor(scoinsBalance / 100) * APP_CONFIG.SCOINS_REDEEM_RATE

    const handleBuyConfirm = () => {
        if (!user) return

        // Simulate purchase
        localDb.scoins.purchase(user.id, buyAmount)
        updateUser({ s_coins: scoinsBalance + buyAmount })

        // Refresh transactions
        const userTransactions = localDb.transactions.getByUserId(user.id)
        setTransactions(userTransactions)

        setShowBuyModal(false)
        setShowPaymentQR(false)
        showToast(`Successfully purchased ${buyAmount} S-Coins!`, 'success')
    }

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('en-IN', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    const getTransactionIcon = (type: SCoinTransaction['type']) => {
        switch (type) {
            case 'earn': return '🎁'
            case 'redeem': return '💸'
            case 'purchase': return '💰'
            default: return '🪙'
        }
    }

    return (
        <div className="container">
            <div className="wallet-page">
                <h1 className="page-title">S-Coins Wallet</h1>

                {/* Balance Cards */}
                <div className="wallet-cards">
                    <div className="wallet-card main-balance">
                        <div className="wallet-card-icon">🪙</div>
                        <div className="wallet-card-content">
                            <span className="wallet-label">Available S-Coins</span>
                            <span className="wallet-value">{scoinsBalance.toLocaleString()}</span>
                            <span className="wallet-subtext">Worth {formatCurrency(scoinsValue)}</span>
                        </div>
                    </div>

                    <div className="wallet-card loyalty-points">
                        <div className="wallet-card-icon">⭐</div>
                        <div className="wallet-card-content">
                            <span className="wallet-label">Lifetime Points</span>
                            <span className="wallet-value">{loyaltyPoints.toLocaleString()}</span>
                            <span className="wallet-subtext">Total earned</span>
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="wallet-actions">
                    <button
                        className="btn btn-gold btn-lg"
                        onClick={() => setShowBuyModal(true)}
                    >
                        💰 Buy S-Coins
                    </button>
                </div>

                {/* How it works */}
                <div className="scoins-info-section">
                    <h2>How S-Coins Work</h2>
                    <div className="info-cards">
                        <div className="info-card">
                            <span className="info-icon">🛒</span>
                            <h3>Earn</h3>
                            <p>Get {APP_CONFIG.SCOINS_EARN_RATE} S-Coins for every ₹100 spent</p>
                        </div>
                        <div className="info-card">
                            <span className="info-icon">💸</span>
                            <h3>Redeem</h3>
                            <p>100 S-Coins = ₹{APP_CONFIG.SCOINS_REDEEM_RATE} discount</p>
                        </div>
                        <div className="info-card">
                            <span className="info-icon">💰</span>
                            <h3>Buy</h3>
                            <p>Purchase S-Coins directly via UPI</p>
                        </div>
                    </div>
                </div>

                {/* Transactions */}
                <div className="transactions-section">
                    <h2>Transaction History</h2>

                    {transactions.length === 0 ? (
                        <div className="empty-transactions">
                            <span>🪙</span>
                            <p>No transactions yet. Start shopping to earn S-Coins!</p>
                        </div>
                    ) : (
                        <div className="transactions-list">
                            {transactions.map(tx => (
                                <div key={tx.id} className="transaction-item">
                                    <div className="tx-icon">{getTransactionIcon(tx.type)}</div>
                                    <div className="tx-details">
                                        <span className="tx-description">
                                            {tx.type === 'earn' && 'Earned from order'}
                                            {tx.type === 'redeem' && 'Redeemed for discount'}
                                            {tx.type === 'purchase' && 'Purchased S-Coins'}
                                        </span>
                                        <span className="tx-date">{formatDate(tx.created_at)}</span>
                                    </div>
                                    <span className={`tx-amount ${tx.amount >= 0 ? 'positive' : 'negative'}`}>
                                        {tx.amount >= 0 ? '+' : ''}{tx.amount}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Buy Modal */}
            {showBuyModal && (
                <div className="modal-backdrop" onClick={() => setShowBuyModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Buy S-Coins</h2>
                            <button className="modal-close" onClick={() => setShowBuyModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            {!showPaymentQR ? (
                                <>
                                    <p className="buy-description">
                                        Purchase S-Coins to use as discount on your orders.
                                    </p>

                                    <div className="buy-options">
                                        {[100, 200, 500, 1000].map(amount => (
                                            <button
                                                key={amount}
                                                className={`buy-option ${buyAmount === amount ? 'selected' : ''}`}
                                                onClick={() => setBuyAmount(amount)}
                                            >
                                                <span className="buy-coins">{amount} 🪙</span>
                                                <span className="buy-price">{formatCurrency(amount)}</span>
                                            </button>
                                        ))}
                                    </div>

                                    <div className="buy-custom">
                                        <label>Or enter custom amount:</label>
                                        <input
                                            type="number"
                                            className="input"
                                            value={buyAmount}
                                            onChange={e => setBuyAmount(Math.max(10, Number(e.target.value)))}
                                            min="10"
                                            step="10"
                                        />
                                    </div>

                                    <div className="buy-summary">
                                        <span>You pay:</span>
                                        <span className="buy-total">{formatCurrency(buyAmount)}</span>
                                    </div>

                                    <button
                                        className="btn btn-primary btn-lg"
                                        style={{ width: '100%' }}
                                        onClick={() => setShowPaymentQR(true)}
                                    >
                                        Proceed to Pay
                                    </button>
                                </>
                            ) : (
                                <>
                                    <div className="payment-qr-section">
                                        <div className="qr-wrapper">
                                            <QRCodeSVG
                                                value={`upi://pay?pa=${APP_CONFIG.UPI_ID}&pn=${encodeURIComponent(APP_CONFIG.STORE_NAME)}&am=${buyAmount}&cu=INR&tn=S-Coins%20Purchase`}
                                                size={180}
                                                level="H"
                                                includeMargin
                                                bgColor="#ffffff"
                                                fgColor="#000000"
                                            />
                                        </div>
                                        <p className="qr-instruction">Scan with any UPI app to pay {formatCurrency(buyAmount)}</p>

                                        <button
                                            className="btn btn-success btn-lg"
                                            style={{ width: '100%', marginTop: 'var(--space-4)' }}
                                            onClick={handleBuyConfirm}
                                        >
                                            I have paid - Confirm Purchase
                                        </button>

                                        <button
                                            className="btn btn-ghost"
                                            style={{ width: '100%', marginTop: 'var(--space-2)' }}
                                            onClick={() => setShowPaymentQR(false)}
                                        >
                                            ← Back
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
